import { GOOGLE_APPS_SCRIPT_WEB_APP_URL } from "./googleAppsScriptWebApp";

export type ButtonPressSource = "main" | "pip" | "mini";

/**
 * Pre-tracking estimate baked into the on-site total.
 *
 * Rough model (site ~5 months old, presses not logged until Apps Script counter):
 * - Month 1: ~2,500 visitors ("more than 2k")
 * - Months 2–5: ~500 each → 500 × 4 = 2,000
 * - Visitors ≈ 2,500 + 2,000 = 4,500
 * - Avg presses per session ≈ 10 (repeat presses are the product)
 * - Offset ≈ 4,500 × 10 = 45,000
 *
 * Tweak any of the inputs below; display = tracked sheet count + this offset.
 */
const PRE_TRACKING_MONTH1_VISITORS = 2500;
const PRE_TRACKING_LATER_MONTHS = 4;
const PRE_TRACKING_LATER_MONTH_VISITORS = 500;
const PRE_TRACKING_PRESSES_PER_SESSION = 10;

export const PRESS_COUNT_DISPLAY_OFFSET = Math.round(
  (PRE_TRACKING_MONTH1_VISITORS +
    PRE_TRACKING_LATER_MONTHS * PRE_TRACKING_LATER_MONTH_VISITORS) *
    PRE_TRACKING_PRESSES_PER_SESSION
);

/**
 * Lifetime button-press counter via the existing Apps Script web app.
 *
 * Requires the script to handle:
 * - GET  ?action=pressCount  → { "count": number }
 * - POST { formType: "button_press", source, timestamp }
 *
 * See `scripts/google-apps-webapp.gs`, then redeploy.
 */
export async function fetchButtonPressCount(): Promise<number | null> {
  try {
    const url = `${GOOGLE_APPS_SCRIPT_WEB_APP_URL}?action=pressCount`;
    const res = await fetch(url, { method: "GET", redirect: "follow" });
    if (!res.ok) return null;
    const data = (await res.json()) as { count?: unknown };
    const count = Number(data.count);
    return Number.isFinite(count) && count >= 0 ? count : null;
  } catch {
    return null;
  }
}

/** Tracked count + pre-tracking offset for display. */
export function displayPressCount(trackedCount: number): number {
  return trackedCount + PRESS_COUNT_DISPLAY_OFFSET;
}

/** Fire-and-forget increment (no-cors — same pattern as feedback / submit). */
export function recordButtonPress(source: ButtonPressSource): void {
  try {
    void fetch(GOOGLE_APPS_SCRIPT_WEB_APP_URL, {
      method: "POST",
      mode: "no-cors",
      body: JSON.stringify({
        formType: "button_press",
        source,
        timestamp: new Date().toISOString(),
      }),
      headers: {
        "Content-Type": "text/plain",
      },
    });
  } catch {
    /* ignore */
  }
}

/** Fire-and-forget per-URL show count (Apps Script bumps a Shows column / LinkShows sheet). */
export function recordLinkShown(url: string): void {
  if (!url) return;
  try {
    void fetch(GOOGLE_APPS_SCRIPT_WEB_APP_URL, {
      method: "POST",
      mode: "no-cors",
      body: JSON.stringify({
        formType: "link_shown",
        url,
        timestamp: new Date().toISOString(),
      }),
      headers: {
        "Content-Type": "text/plain",
      },
    });
  } catch {
    /* ignore */
  }
}

export function formatPressCount(count: number): string {
  return count.toLocaleString("en-US");
}
